import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardQuickActions } from '@/features/admin/components/DashboardQuickActions';
import { DashboardRecentSales } from '@/features/admin/components/DashboardRecentSales';
import {
  DashboardStats,
  type DashboardMetric,
} from '@/features/admin/components/DashboardStats';
import { useDashboardData } from '@/features/admin/hooks/useDashboardData';

const metricIcon = (name: string) => (
  <span className="material-symbols-outlined text-[24px]">{name}</span>
);

export const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('vi') ? 'vi-VN' : 'en-US';
  const {
    permissions,
    salesScope,
    sales,
    productTotal,
    userStatistics,
    retrySales,
    retryProductTotal,
    retryUserStatistics,
  } = useDashboardData();

  const currentDate = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [locale],
  );

  const metrics: DashboardMetric[] = [];

  if (salesScope) {
    metrics.push({
      key: 'sales',
      label: t(`admin.dashboard.stats.sales_${salesScope}`),
      detail: t(`admin.dashboard.stats.sales_${salesScope}_detail`),
      value: sales.data?.totalElements ?? null,
      icon: metricIcon('receipt_long'),
      iconClass: 'bg-sky-50 text-sky-600',
      isLoading: sales.isLoading,
      error: sales.error,
      onRetry: retrySales,
    });
  }

  if (permissions.canReadProducts) {
    metrics.push({
      key: 'products',
      label: t('admin.dashboard.stats.products'),
      detail: t('admin.dashboard.stats.products_detail'),
      value: productTotal.data,
      icon: metricIcon('inventory_2'),
      iconClass: 'bg-amber-50 text-amber-600',
      isLoading: productTotal.isLoading,
      error: productTotal.error,
      onRetry: retryProductTotal,
    });
  }

  if (permissions.canReadUsers) {
    metrics.push({
      key: 'users',
      label: t('admin.dashboard.stats.users'),
      detail: t('admin.dashboard.stats.users_detail'),
      value: userStatistics.data?.total ?? null,
      icon: metricIcon('group'),
      iconClass: 'bg-violet-50 text-violet-600',
      isLoading: userStatistics.isLoading,
      error: userStatistics.error,
      onRetry: retryUserStatistics,
    });
  }

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            {t('admin.dashboard.title')}
          </h1>
          <p className="mt-1.5 max-w-2xl text-sm leading-6 text-slate-500">
            {t('admin.dashboard.subtitle')}
          </p>
        </div>
        <div className="inline-flex w-fit items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 shadow-sm">
          <span className="material-symbols-outlined text-xl text-primary" aria-hidden="true">
            calendar_today
          </span>
          <span className="sr-only">{t('admin.dashboard.current_date')}</span>
          <time dateTime={new Date().toISOString().slice(0, 10)}>{currentDate}</time>
        </div>
      </header>

      <DashboardStats metrics={metrics} locale={locale} />

      <div
        className={`grid min-w-0 gap-6 ${
          permissions.canReadSales ? 'xl:grid-cols-[320px_minmax(0,1fr)]' : ''
        }`}
      >
        <DashboardQuickActions
          canCreateSale={permissions.canCreateSale}
          canReadSales={permissions.canReadSales}
          canReadProducts={permissions.canReadProducts}
          canReadUsers={permissions.canReadUsers}
        />

        {salesScope && (
          <DashboardRecentSales
            page={sales.data}
            scope={salesScope}
            isLoading={sales.isLoading}
            error={sales.error}
            onRetry={retrySales}
          />
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
